import { parseAMIFile } from './parser';
import type { AMIParseResult, ParseOptions } from './parser';
import { buildLayoutSample, instructionsFromProposal, layoutKeyOf, reconcile } from './instructions';
import type { LayoutProposal, LayoutSample } from './instructions';

/**
 * Reading an uploaded file with the layout library and, for a layout nobody has
 * seen, Claude's proposal. The parser alone is parseAMIFile; this is the
 * upload's full path around it. See instructions.ts for the rules.
 *
 *   1. The parser reads the file as it always has.
 *   2. The preparer's own column choice is final: nothing else runs.
 *   3. A known layout (same header row) is read from the library's saved
 *      instructions. If they no longer read the file, the layout is retired and
 *      the file is treated as never seen.
 *   4. A never-seen layout gets one proposal from Claude, when the switch is on.
 *      Readings that agree save the layout. Readings that differ keep the
 *      parser's and flag it. A reading only Claude's instructions can make is
 *      used behind a verify flag, and saved once the preparer confirms.
 *
 * Waiting is bounded. A file the parser read is held for at most
 * AGREEMENT_WAIT_MS while Claude answers; a file it could not read, for at most
 * RESCUE_WAIT_MS. Past that the parser's result stands and the upload goes on.
 */

export const AGREEMENT_WAIT_MS = 20_000;
export const RESCUE_WAIT_MS = 45_000;

export interface SavedLayout {
  proposal: LayoutProposal;
  sample: LayoutSample;
}

export interface Proposed {
  id: string;
  proposal: LayoutProposal;
}

/** The outside world readFile needs, injected so the path is testable. */
export interface ReadDeps {
  aiEnabled: () => Promise<boolean>;
  lookup: (layoutKey: string) => Promise<SavedLayout | null>;
  propose: (layoutKey: string, sample: LayoutSample, waitMs: number) => Promise<Proposed | null>;
  save: (proposalId: string, source: 'agreement' | 'ai', utility: string | null) => Promise<void>;
  retire: (layoutKey: string, reason: string) => Promise<void>;
}

export interface ReadOutcome {
  result: AMIParseResult;
  /** The library key of the file's header row, when it has one. */
  layoutKey: string | null;
  /** A proposal to save once the preparer confirms the reading it made. */
  pendingSave: { proposalId: string } | null;
}

const ok = (r: AMIParseResult) => r.errors.length === 0 && r.data.length > 0;

export async function readFile(
  content: string | ArrayBuffer,
  filename: string,
  options: Pick<ParseOptions, 'columnOverride'> & { utility: string | null },
  deps: ReadDeps
): Promise<ReadOutcome> {
  const first = await parseAMIFile(content, filename, options.columnOverride ? { columnOverride: options.columnOverride } : {});
  const layoutKey = await layoutKeyOf(first.metadata?.fingerprintHeader as (string | null)[] | null | undefined).catch(() => null);
  if (options.columnOverride || !layoutKey) return { result: first, layoutKey, pendingSave: null };

  // ── 3. A known layout ────────────────────────────────────────────────────
  const saved = await deps.lookup(layoutKey).catch(() => null);
  if (saved) {
    const instructions = instructionsFromProposal(saved.proposal, saved.sample);
    if (instructions) {
      const fromLibrary = await parseAMIFile(content, filename, {
        layout: instructions.layout, columnOverride: instructions.columns, instructionsSource: 'library',
      });
      if (ok(fromLibrary)) return { result: fromLibrary, layoutKey, pendingSave: null };
      void deps.retire(layoutKey, `Saved instructions did not read a file with this header: ${fromLibrary.errors[0] ?? 'no readings'}`).catch(() => {});
    } else {
      void deps.retire(layoutKey, 'Saved instructions no longer validate against their sample').catch(() => {});
    }
  }

  // ── 4. A never-seen layout ───────────────────────────────────────────────
  if (!(await deps.aiEnabled().catch(() => false))) return { result: first, layoutKey, pendingSave: null };
  const source = first.metadata?.layoutSampleSource;
  if (!source) return { result: first, layoutKey, pendingSave: null };
  const sample = buildLayoutSample(source.rows, source.firstRowIndex, options.utility);
  const proposed = await deps.propose(layoutKey, sample, ok(first) ? AGREEMENT_WAIT_MS : RESCUE_WAIT_MS).catch(() => null);
  if (!proposed || !proposed.proposal.confident) return { result: first, layoutKey, pendingSave: null };
  const instructions = instructionsFromProposal(proposed.proposal, sample);
  if (!instructions) return { result: first, layoutKey, pendingSave: null };

  const fromProposal = await parseAMIFile(content, filename, {
    layout: instructions.layout, columnOverride: instructions.columns, instructionsSource: 'ai',
  });
  const outcome = reconcile(first, fromProposal, proposed.proposal.description);
  switch (outcome.kind) {
    case 'agree':
      void deps.save(proposed.id, 'agreement', options.utility).catch(() => {});
      return { result: outcome.result, layoutKey, pendingSave: null };
    case 'ai-only':
      return { result: outcome.result, layoutKey, pendingSave: { proposalId: proposed.id } };
    default:
      return { result: outcome.result, layoutKey, pendingSave: null };
  }
}
